import { displayDate } from "src/utils/dateHandler.util";
import { getLangNameByLangCode } from "src/utils/util";
import { Slot } from "../types/slot.entity";
import { Link, useLocation } from "react-router-dom";
import { FaExternalLinkAlt } from "react-icons/fa";
import { appendSearchToPath } from "src/customHooks/usePaginatedTableSearchParams";

type Props = {
  data: (Slot & { id: string })[];
};

const statusBadgeClass = (status: Slot["status"]) => {
  switch (status) {
    case "AVAILABLE":
      return "bg-emerald-100 text-emerald-700 border-emerald-200";
    case "BOOKED":
      return "bg-blue-100 text-blue-700 border-blue-200";
    case "EXPIRED":
      return "bg-gray-100 text-gray-700 border-gray-200";
    case "SINGLE_REQUEST_PENDING":
      return "bg-amber-100 text-amber-800 border-amber-200";
    default:
      return "bg-gray-100 text-gray-700 border-gray-200";
  }
};

const coachTypeClass = (coachType: Slot["coachType"]) =>
  coachType === "PRO"
    ? "bg-yellow-100 text-yellow-800 border-yellow-200"
    : "bg-teal-100 text-teal-700 border-teal-200";

export const SlotsTable: React.FC<Props> = ({ data }) => {
  const { search } = useLocation();

  return (
    <div className="overflow-x-auto mb-6">
      <table className="min-w-full border border-gray-200 divide-y divide-gray-200">
        <thead className="bg-gray-900 text-white text-center">
          <tr>
            <th className="px-4 py-3 uppercase text-xs tracking-wider">Slot</th>
            <th className="px-4 py-3 uppercase text-xs tracking-wider">User</th>
            <th className="px-4 py-3 uppercase text-xs tracking-wider">Lang</th>
            <th className="px-4 py-3 uppercase text-xs tracking-wider">Status</th>
            <th className="px-4 py-3 uppercase text-xs tracking-wider">Coach</th>
            <th className="px-4 py-3 uppercase text-xs tracking-wider">Created</th>
            <th className="px-4 py-3 uppercase text-xs tracking-wider">When</th>
            <th className="px-4 py-3 uppercase text-xs tracking-wider">Actions</th>
          </tr>
        </thead>

        <tbody>
          {data.length === 0 && (
            <tr>
              <td colSpan={8} className="text-center py-4 text-xl text-gray-500">
                No Slots
              </td>
            </tr>
          )}

          {data.map((slot) => {
            const scheduledAt = slot.date instanceof Date ? slot.date : (slot.date as any).toDate?.() ?? undefined;
            const createdAt = slot.createdAt instanceof Date ? slot.createdAt : (slot.createdAt as any).toDate?.() ?? undefined;
            return (
              <tr key={slot.id} className="text-center odd:bg-white even:bg-gray-50 hover:bg-gray-100">
                <td className="px-4 py-3 border-t border-gray-200 font-mono text-xs" title={slot.id}>
                  {slot.id}
                </td>
                <td className="px-4 py-3 border-t border-gray-200">
                  <Link
                    to={`/users/${slot.userId}`}
                    className="inline-flex items-center px-2.5 py-1 rounded-full bg-gradient-to-r from-cyan-500 via-sky-500 to-indigo-500 text-white border border-sky-300 shadow-sm hover:shadow-md hover:from-cyan-600 hover:via-sky-600 hover:to-indigo-600 transition-all duration-200 transform hover:scale-110 will-change-transform no-underline font-mono font-semibold text-[10px] sm:text-[11px] focus:outline-none focus:ring-2 focus:ring-sky-300 focus:ring-offset-1 select-text"
                  >
                    {slot.userId}
                  </Link>
                </td>
                <td className="px-4 py-3 border-t border-gray-200">
                  <div className="flex flex-col leading-tight">
                    <span className="font-medium">{getLangNameByLangCode(slot.langCode)}</span>
                    <span className="text-xs text-gray-500">{slot.langCode}</span>
                  </div>
                </td>
                <td className="px-4 py-3 border-t border-gray-200">
                  <span className={`inline-block px-2 py-1 rounded-full border text-xs font-semibold ${statusBadgeClass(
                    slot.status
                  )}`}>
                    {slot.status.replaceAll("_", " ")}
                  </span>
                </td>
                <td className="px-4 py-3 border-t border-gray-200">
                  <span className={`inline-block px-2 py-1 rounded-full border text-xs font-semibold ${coachTypeClass(
                    slot.coachType
                  )}`}>
                    {slot.coachType}
                  </span>
                </td>
                <td className="px-4 py-3 border-t border-gray-200">
                  <div className="flex flex-col leading-tight">
                    <span>{createdAt ? createdAt.toLocaleDateString() : displayDate(slot.createdAt)}</span>
                    <span className="text-xs text-gray-500">{createdAt ? createdAt.toLocaleTimeString() : ""}</span>
                  </div>
                </td>
                <td className="px-4 py-3 border-t border-gray-200">
                  <div className="flex flex-col leading-tight">
                    <span>{scheduledAt ? scheduledAt.toLocaleDateString() : displayDate(slot.date)}</span>
                    <span className="text-xs text-gray-500">{scheduledAt ? scheduledAt.toLocaleTimeString() : ""}</span>
                  </div>
                </td>
                <td className="px-4 py-3 border-t border-gray-200">
                  <Link
                    to={appendSearchToPath(`/slots/${slot.id}`, search)}
                    className="inline-flex items-center justify-center px-4 py-2 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-medium rounded-lg shadow-md hover:shadow-lg transform hover:scale-105 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-emerald-300 focus:ring-offset-2"
                  >
                    <FaExternalLinkAlt />
                  </Link>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default SlotsTable;

